let Sentry = null;
let sentryEnabled = false;
const pendingExceptions = [];
const MAX_PENDING = 50;

function flushPending() {
  if (!Sentry) return;
  while (pendingExceptions.length) {
    const { error, context } = pendingExceptions.shift();
    try {
      Sentry.captureException(error, { extra: context });
    } catch (_) {
      // never let reporting throw
    }
  }
}

export function initObservability() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return null;

  sentryEnabled = true;

  // Defer loading the Sentry SDK so it stays out of the initial JS bundle.
  // It is fetched as a separate chunk once the browser is idle / after first paint.
  const load = () => {
    import(/* webpackChunkName: "sentry" */ "@sentry/react")
      .then((mod) => {
        Sentry = mod;
        Sentry.init({
          dsn,
          environment:
            process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "production",
          release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
          tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0),
          replaysSessionSampleRate: 0,
          replaysOnErrorSampleRate: 0,
          ignoreErrors: [
            // Sentry's onboarding/test error (often injected by browser
            // extensions or pasted into the console). Never record it.
            "Sentry test error - ignore me",
            // Browser/environment storage failure (corrupted profile, restricted
            // storage, some incognito/extension states), not an app bug. Firebase
            // Auth falls back to in-memory persistence, so the app still works.
            "Internal error opening backing store for indexedDB.open",
          ],
        });
        flushPending();
      })
      .catch((err) => {
        sentryEnabled = false;
        if (process.env.NODE_ENV === "development") {
          console.warn("Sentry init failed:", err?.message);
        }
      });
  };

  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(load, { timeout: 3000 });
  } else if (typeof window !== "undefined") {
    window.setTimeout(load, 1500);
  } else {
    load();
  }

  return null;
}

export function captureException(error, context = {}) {
  if (!sentryEnabled) return;
  if (Sentry) {
    try {
      Sentry.captureException(error, { extra: context });
    } catch (_) {
      // never let reporting throw
    }
    return;
  }
  // Sentry chunk not loaded yet — queue (capped) and flush once it loads.
  if (pendingExceptions.length < MAX_PENDING) {
    pendingExceptions.push({ error, context });
  }
}

export function getSentry() {
  return Sentry;
}
